/* eslint-disable react/require-default-props */
import * as React from 'react';
import { map } from 'lodash';

import DatePicker from './date-picker';
import CountrySelect from './country-select';
import TimeZoneSelect from './time-zone-select';
import SportSelect from './sport-select';
import { Checkbox, Radio } from '@teamsnap/teamsnap-ui';
import './form-element.scss';

/**
 * InputType specifies all of the "type" fields that this component currently supports
 * When adding support for a new field, you'll want to add it to list for consumption as well.
 */

export interface Props {
  label?: string | JSX.Element;
  labelHidden?: boolean;
  useHookResponse?: any; // TODO: Need the actual type signature. Haha, you should see that gnarly thing.
  field?: string;
  fieldSecondary?: string;
  type:
    | 'text'
    | 'email'
    | 'select'
    | 'gender'
    | 'checkbox'
    | 'radio'
    | 'date'
    | 'state'
    | 'phone'
    | 'textarea'
    | 'password'
    | 'country'
    | 'timezone'
    | 'sport'
    | 'number';
  maxLength?: number;
  className?: string;
  inputClassName?: string;
  options?: { text: string; value: string }[];
  dateProps?: any;
  isInline?: boolean;
  onUpdate?: (string) => void;
  inputRef?: (ref: HTMLSelectElement | HTMLInputElement) => any;
  placeholder?: string;
  disabled?: boolean;
  checked?: boolean;
  defaultValue?: any;
  onChange?: (any) => void;
  hasExternalError?: boolean;
  autoComplete?: string;
  spellCheck?: boolean;
  numberInputAttributes?: { min?: number; max?: number; step?: number };
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  isNegativeNumber?: boolean;
  onSignChange?: (boolean) => void;
  onKeyDown?: (e: any) => void;
}

interface IMyComponentState {
  isNegativeNumber?: boolean;
}

/**
 * Due to the way we're using react hook form with our custom components, we cant
 * dependably use default values, so we use this instead:
 * https://react-hook-form.com/api/#register
 */
export const useFormDefaults = (dataset: any, setupFn: (...args) => void, dependencyArray: Array<any>) => {
  React.useEffect(() => {
    if (dataset) {
      // the timing for this is really weird, so we bounce this
      // to the end of the event loop via set timeout
      setTimeout(() => {
        setupFn(dataset);
      }, 0);
    }
  }, dependencyArray);
};

/**
 * FormElementHook provides a simple way to build robust and full-featured form inputs
 * while leveraging the awesomeness that is react-hook-form.
 */
export default class FormElementHook extends React.Component<Props, IMyComponentState> {
  /**
   * This input ref gets passed on the components and is required by react-hook-form.
   */
  inputRef: React.RefObject<any>;

  constructor(props: Props) {
    super(props);
    this.inputRef = React.createRef();
    this.state = { isNegativeNumber: false };
  }

  UNSAFE_componentWillUpdate() {
    if (
      Object.keys(this.getErrors(this.props)).length > 0 &&
      this.inputRef.current &&
      typeof this.inputRef.current.scrollIntoView == 'function'
    ) {
      this.inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (
      Object.keys(this.getErrors(this.props)).length > 0 &&
      this.inputRef.current &&
      typeof this.inputRef.current.scrollIntoView == 'function'
    ) {
      this.inputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (prevProps.isNegativeNumber !== this.props.isNegativeNumber) {
      this.setState({ isNegativeNumber: this.props.isNegativeNumber ?? false });
    }
  }

  componentDidMount() {
    if (this.props.inputRef) {
      this.props.inputRef(this.inputRef.current);
    }
  }

  private getErrors(props: Props): { message: string } {
    return (props?.useHookResponse?.errors || {})[props?.field as string] || {};
  }

  private renderElement() {
    const {
      autoComplete,
      useHookResponse,
      field,
      type,
      inputClassName,
      options,
      dateProps,
      label,
      maxLength,
      isInline,
      placeholder,
      disabled,
      defaultValue,
      spellCheck,
      numberInputAttributes,
      leftIcon,
      rightIcon,
      onSignChange,
      onChange,
      onKeyDown,
    } = this.props;

    const fieldName = field?.toString();
    const className = inputClassName || '';

    if (type === 'select') {
      return (
        <div className="SelectBox">
          <select
            ref={this.inputRef}
            className={'SelectBox-options ' + className}
            name={fieldName}
            id={fieldName}
            disabled={disabled}
            onChange={this.props.onChange}
            data-testid={`selectbox-options-${fieldName}`}
          >
            {map(options, (o) => (
              <option key={o.value} value={o.value}>
                {o.text}
              </option>
            ))}
          </select>
        </div>
      );
    }

    if (type === 'gender') {
      return (
        <div className="SelectBox">
          <select
            ref={this.inputRef}
            className={className + ' SelectBox-options gender'}
            name={fieldName}
            disabled={disabled}
            data-testid={`selectbox-options-${fieldName}`}
          >
            <option value="" />
            <option value="male">Male</option>
            <option value="female">Female</option>
          </select>
        </div>
      );
    }

    if (type === 'email') {
      return (
        <input
          onChange={onChange}
          ref={this.inputRef}
          className={'Input ' + className}
          maxLength={150}
          type="email"
          id={fieldName}
          name={fieldName}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          data-testid={`input-${fieldName}`}
        />
      );
    }

    if (type === 'date') {
      // TODO: Get validation working for date fields
      // Validation and required fields do not work with the datepicker component.
      return (
        <DatePicker
          useHookResponse={useHookResponse}
          inputRef={this.inputRef}
          name={fieldName}
          dateProps={dateProps}
          className="Input"
          disabled={disabled}
          placeholderText={placeholder}
        />
      );
    }

    if (type === 'checkbox') {
      return (
        <Checkbox
          name={field}
          inputProps={{
            disabled: disabled,
            ref: this.inputRef,
          }}
          testId={`checkbox-${fieldName}`}
          label={label}
          mods={'u-padBottomNone'}
          isInline={isInline}
          {...{ id: fieldName, disabled: disabled }}
        />
      );
    }

    if (type === 'radio') {
      return map(options, (option) => (
        <Radio
          key={option.value}
          name={option.value}
          label={option.text}
          group={field}
          isInline={isInline}
          testId={`radio-${fieldName}`}
          inputProps={{
            ref: this.inputRef,
          }}
          {...{ id: fieldName, disabled: disabled }}
        />
      ));
    }

    if (type === 'country') {
      return <CountrySelect id={fieldName} name={fieldName} inputRef={this.inputRef} disabled={disabled} />;
    }

    if (type === 'timezone') {
      return <TimeZoneSelect id={fieldName} name={fieldName} inputRef={this.inputRef} disabled={disabled} />;
    }

    if (type === 'sport') {
      return <SportSelect id={fieldName} name={fieldName} inputRef={this.inputRef} disabled={disabled} />;
    }

    if (type === 'textarea') {
      return (
        <textarea
          ref={this.inputRef}
          className={'Input ' + className}
          maxLength={maxLength}
          name={fieldName}
          id={fieldName}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          spellCheck={spellCheck}
          data-testid={`textarea-${fieldName}`}
        />
      );
    }

    if (type === 'password') {
      return (
        <input
          onChange={onChange}
          ref={this.inputRef}
          className={'Input ' + className}
          maxLength={260}
          type="password"
          name={fieldName}
          id={fieldName}
          disabled={disabled}
          autoComplete={autoComplete}
          data-testid={`text-${fieldName}`}
        />
      );
    }

    if (type === 'number') {
      return (
        <div
          className={`${rightIcon && 'InputGroup--rightIcon'} ${leftIcon && 'InputGroup--leftIcon'} ${
            onSignChange && 'InputGroup--allowNegatives'
          } `}
        >
          {onSignChange && (
            <button
              className={`pos-neg-toggle ${this.state.isNegativeNumber ? 'u-colorNegative' : 'u-colorSuccess6'} `}
              type="button"
              onClick={() => {
                this.setState({
                  isNegativeNumber: !this.state.isNegativeNumber,
                });
                onSignChange(!this.state.isNegativeNumber);
              }}
              data-testid="pos-neg-toggle"
            >
              {this.state.isNegativeNumber ? '-' : '+'}
            </button>
          )}
          {leftIcon && <div className="InputGroup-icon--left InputGroup-icon u-spaceTopXs">{leftIcon}</div>}
          <input
            ref={this.inputRef}
            className={'Input ' + className}
            type="number"
            name={fieldName}
            id={fieldName}
            disabled={disabled}
            min={numberInputAttributes?.min || 0}
            max={numberInputAttributes?.max || 999999}
            step={numberInputAttributes?.step || 0.01}
            onChange={onChange}
            onKeyDown={onKeyDown}
            onKeyPress={(event) => {
              if (
                event.key === '+' ||
                event.key === '-' ||
                event.key.toLowerCase() === 'e' ||
                event.currentTarget.value.length >= (numberInputAttributes?.max || 9)
              )
                event.preventDefault();
            }}
            autoComplete={autoComplete}
            placeholder={placeholder}
            data-testid={`number-${fieldName}`}
          />
          {rightIcon && (
            <div className="InputGroup-icon--right InputGroup-icon u-spaceTopXs u-spaceRightMd">{rightIcon}</div>
          )}
        </div>
      );
    }

    return (
      <div className={leftIcon ? 'InputGroup--leftIcon' : ''}>
        {leftIcon && <div className="InputGroup-icon--left InputGroup-icon sui-h-0">{leftIcon}</div>}
        <input
          onChange={onChange}
          ref={this.inputRef}
          className={'Input ' + className}
          type="text"
          maxLength={maxLength}
          name={fieldName}
          id={fieldName}
          placeholder={placeholder}
          disabled={disabled}
          autoComplete={autoComplete}
          data-testid={`input-${fieldName}`}
        />
      </div>
    );
  }

  render() {
    const { className, label, field, type, labelHidden, isInline, hasExternalError } = this.props;

    const fieldErrors = this.getErrors(this.props) || { message: null };
    const fieldName = field?.toString();
    const showLabel = label && type !== 'checkbox';

    return (
      <div
        className={
          `FieldGroup ${isInline ? 'u-flex u-flexAlignItemsCenter' : ''} ${fieldName} ` +
          (Object.keys(fieldErrors).length > 0 || hasExternalError ? 'error' : '') +
          (className || '')
        }
      >
        {showLabel && (
          <label className={`FieldGroup-label ${labelHidden ? 'u-hiddenVisually' : ''}`} htmlFor={fieldName}>
            {label}
          </label>
        )}

        {this.renderElement()}

        {fieldErrors && fieldErrors.message && (
          <div data-testid={`${fieldName}-validation-error`} className={`validation-error ${isInline ? ' u-spaceLeftMd' : ''}`}>
            {fieldErrors.message}
          </div>
        )}
      </div>
    );
  }
}
